import { useState, type DragEvent } from 'react'
import { Icon } from '../../ui/Icon'
import { Modal } from '../../ui/Modal'
import { Thumb } from '../../ui/Thumb'
import { failureText } from '../../ui/Load'
import { useDeletePhoto, useUploadPhoto, type Product, type ProductPhoto } from './api'
import { PHOTO_TYPES, sortPhotos } from './model'

export interface ProductPhotosProps {
  item: Product
  canUpdate: boolean
  onToast: (message: string) => void
}

/**
 * Фотографии товара: первая — главная, её видно в каталоге. Файлы можно
 * перетащить на блок или выбрать — сразу несколько; уходят по одному, так
 * сервер принимает фото. Загрузка не ждёт кнопки «Сохранить»: файл — не
 * черновик, его нельзя «не сохранить».
 */
export function ProductPhotos({ item, canUpdate, onToast }: ProductPhotosProps) {
  const upload = useUploadPhoto()
  const removePhoto = useDeletePhoto()
  // Какое фото ждёт подтверждения удаления: убрать фото нельзя одним
  // случайным нажатием, вернуть его можно только новой загрузкой.
  const [removing, setRemoving] = useState<ProductPhoto | null>(null)
  const [error, setError] = useState('')
  // Сколько вложенных элементов под курсором с файлом: dragleave приходит и
  // при переходе на дочерний элемент, счётчик не даёт подсветке мигать.
  const [depth, setDepth] = useState(0)
  const [sending, setSending] = useState(0)
  const count = item.photoItems.length

  async function send(files: File[]) {
    const { good, error: rejected } = sortPhotos(files)
    setError(rejected)
    let done = 0
    for (const file of good) {
      setSending(good.length - done)
      try {
        await upload.mutateAsync({ id: item.id, file })
        done += 1
      } catch (failure) {
        onToast(failureText(failure))
        break
      }
    }
    setSending(0)
    if (done) {
      onToast(done === 1 ? 'Фотография загружена' : `Загружено фото: ${done}`)
    }
  }

  const drop = canUpdate
    ? {
        onDragEnter: (event: DragEvent) => {
          event.preventDefault()
          setDepth((d) => d + 1)
        },
        onDragOver: (event: DragEvent) => {
          event.preventDefault()
        },
        onDragLeave: () => {
          setDepth((d) => d - 1)
        },
        onDrop: (event: DragEvent) => {
          event.preventDefault()
          setDepth(0)
          void send([...event.dataTransfer.files])
        },
      }
    : {}

  const picker = (
    <input
      type="file"
      className="visually-hidden"
      multiple
      accept={PHOTO_TYPES.join(',')}
      aria-label="Добавить фото"
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? 'pd-photo-error' : undefined}
      disabled={sending > 0}
      onChange={(e) => {
        const files = [...(e.target.files as FileList)]
        // Поле сбрасываем сразу: иначе тот же файл второй раз не выбрать.
        e.target.value = ''
        void send(files)
      }}
    />
  )

  return (
    <section
      className="ui-card ui-drop-card"
      aria-labelledby="pd-photos"
      aria-busy={sending > 0}
      data-drop={depth > 0 || undefined}
      {...drop}
    >
      <div className="ui-card-head">
        <h3 id="pd-photos">
          Фотографии
          {count ? <span className="ui-count">{count}</span> : null}
        </h3>
        {sending ? <span className="aside">Загружаем: {sending}…</span> : null}
      </div>
      <div className="ui-card-body pd-photo-body">
        {count === 0 && !canUpdate ? (
          <p className="ui-muted-line">Фото нет — на витрине товар без картинки</p>
        ) : null}

        {count > 0 ? (
          <ul className="pd-photos">
            {item.photoItems.map((photo, i) => (
              <li key={photo.id}>
                {/* Файл не загрузился — нейтральная заглушка, а не подпись
                    картинки поверх квадрата. */}
                <Thumb photos={[photo.url]} alt={`${item.name}: фото ${i + 1}`} fluid />
                {i === 0 ? <span className="ui-tag pd-main">главное</span> : null}
                {canUpdate ? (
                  <button
                    type="button"
                    className="ui-iconbtn pd-remove"
                    aria-label="Удалить фото"
                    data-tip="Удалить фото"
                    onClick={() => {
                      setRemoving(photo)
                    }}
                  >
                    <Icon name="i-trash" />
                  </button>
                ) : null}
              </li>
            ))}
            {canUpdate ? (
              <li>
                <label className="ui-drop ui-drop-tile">
                  <Icon name="i-plus" />
                  <span>Добавить</span>
                  {picker}
                </label>
              </li>
            ) : null}
          </ul>
        ) : null}

        {count === 0 && canUpdate ? (
          <label className="ui-drop">
            <Icon name="i-img" />
            <b>Перетащите фото сюда</b>
            <span>
              или <u>выберите файлы</u> · JPEG, PNG, WebP до 5 МБ
            </span>
            {picker}
          </label>
        ) : null}

        {error ? (
          <span className="ui-error" id="pd-photo-error" role="alert">
            <Icon name="i-alert" />
            {error}
          </span>
        ) : null}
      </div>

      {depth > 0 ? (
        <div className="ui-drop-over" aria-hidden="true">
          <Icon name="i-img" />
          Отпустите, чтобы загрузить
        </div>
      ) : null}

      {removing ? (
        <Modal
          title="Удалить фото?"
          onClose={() => {
            setRemoving(null)
          }}
        >
          <p>
            Фото пропадёт из карточки товара и с витрины. Вернуть его можно только новой загрузкой.
          </p>
          <img className="ui-thumb pd-confirm" src={removing.url} alt="" />
          <div className="mfoot">
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              onClick={() => {
                setRemoving(null)
              }}
            >
              Не удалять
            </button>
            <button
              type="button"
              className="ui-btn"
              data-variant="danger"
              disabled={removePhoto.isPending}
              aria-busy={removePhoto.isPending}
              onClick={() => {
                removePhoto.mutate(
                  { id: item.id, photoId: removing.id },
                  {
                    onSuccess: () => {
                      onToast('Фото удалено')
                    },
                    onError: (failure) => {
                      onToast(failureText(failure))
                    },
                    // Окно закрываем, только когда сервер ответил: пока идёт
                    // запрос, кнопка занята и повторно не нажимается.
                    onSettled: () => {
                      setRemoving(null)
                    },
                  },
                )
              }}
            >
              Удалить фото
            </button>
          </div>
        </Modal>
      ) : null}
    </section>
  )
}
